import { signOut } from "aws-amplify/auth/cognito";

import { notifications } from "@mantine/notifications";
import { useMutation } from "@tanstack/react-query";
import { usePreferenceStorage } from "@/setup/frontend/storage/PreferenceStorageContext";
import { useCacheStorage } from "@/setup/frontend/storage/CacheStorageContext";
import { useLogger } from "@/logging/client/LoggerContext";
import { modals } from "@mantine/modals";
import { Text } from "@mantine/core";

export function useLogout() {
	const preferenceStorage = usePreferenceStorage();
	const cacheStorage = useCacheStorage();
	const logger = useLogger();
	const logout = useMutation({
		mutationFn: async () => {
			return new Promise<boolean>((resolve, reject) => {
				modals.openConfirmModal({
					title: "Confirm Logout",
					children: (
						<Text size="sm">
							Logging out will clear ALL offline data. Are you sure you want to
							logout?
						</Text>
					),
					labels: { confirm: "Logout", cancel: "Do Not Logout" },
					confirmProps: { color: "red" },
					cancelProps: { color: "gray" },
					closeOnClickOutside: false,
					onCancel: () => resolve(false),
					onConfirm: async () => {
						try {
							logger.info("Logout: user-initiated logout");
							await logger.flush().catch((e) => console.error(e));

							await signOut().catch((e) => {
								logger.error(
									`Logout: signOut failed - ${e instanceof Error ? e.message : "unknown error"}`,
								);
							});

							await Promise.all([
								preferenceStorage.clear().catch((e) => {}),
								cacheStorage.clear().catch((e) => {}),
							]);
							resolve(true);
						} catch (error) {
							reject(error);
						}
					},
				});
			});
		},
		onSuccess: (confirmed) => {
			if (confirmed) {
				window.location.href = "/login";
			}
		},
		onError: (error) => {
			notifications.show({
				title: "Logout failed",
				message: error.message,
				color: "red",
			});
		},
		networkMode: "always",
	});
	return logout;
}
